import { useEffect } from 'react';

import useChat from '@hooks/useChat';

import { useParams } from 'react-router-dom';

import {
  SubscribeQuotationChatRoomDocument,
  SubscribeQuotationChatRoomSubscription,
  SubscribeQuotationChatRoomSubscriptionVariables,
  useFetchQuotationChatRoomQuery
} from '@generated/graphql';

import { setMenuStatus } from './utils';

import { formatDate } from '@utils/formatter';
import useChatMessages from './useChatMessages';

const useSubscribeChatRoom = () => {
  const channelIo = document.getElementById('ch-plugin');

  const { onUpdateChatInfo, onInitChatInfo } = useChat();
  const { onReadChatMessages } = useChatMessages();

  const { chatRoomId } = useParams() as { chatRoomId: string };

  const {
    data: fetchData,
    error,
    loading,
    subscribeToMore
  } = useFetchQuotationChatRoomQuery({
    variables: {
      input: {
        chatRoomId
      }
    },
    skip: !chatRoomId,
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    subscribeToMore<
      SubscribeQuotationChatRoomSubscription,
      SubscribeQuotationChatRoomSubscriptionVariables
    >({
      document: SubscribeQuotationChatRoomDocument,
      variables: { input: { chatRoomId: chatRoomId ?? '' } },
      updateQuery: (
        prevResult,
        { subscriptionData: { data: subscribeData } }
      ) => {
        if (!subscribeData) return prevResult;

        const curr = subscribeData.subscribeQuotationChatRoom.data ?? undefined;

        const prev = prevResult.fetchQuotationChatRoom?.data ?? undefined;

        const prevPartnerLastSeenAt = prev?.partnerLastSeenAt
          ? new Date(formatDate(prev?.partnerLastSeenAt, 'YYYY-MM-DD HH:mm:ss'))
          : null;

        const currPartnerLastSeenAt = curr?.partnerLastSeenAt?.split('.')?.[0]
          ? new Date(curr?.partnerLastSeenAt?.split('.')?.[0])
          : null;

        // 가변 데이터 비교 후 업데이트
        if (prev?.isPurchaseOrderConfirmed !== curr?.isPurchaseOrderConfirmed) {
          onUpdateChatInfo({
            isPurchaseOrderConfirmed: curr?.isQuotationConfirmed,
            menuStatus: setMenuStatus(curr)
          });
        }

        if (prev?.isQuotationConfirmed !== curr?.isQuotationConfirmed) {
          onUpdateChatInfo({
            isQuotationConfirmed: curr?.isQuotationConfirmed,
            menuStatus: setMenuStatus(curr)
          });
        }

        if (prev?.hasPurchaseOrder !== curr?.hasPurchaseOrder) {
          onUpdateChatInfo({
            hasPurchaseOrder: curr?.hasPurchaseOrder,
            menuStatus: setMenuStatus(curr)
          });
        }

        if (prev?.hasQuotation !== curr?.hasQuotation) {
          onUpdateChatInfo({
            hasQuotation: curr?.hasQuotation,
            menuStatus: setMenuStatus(curr)
          });
        }

        if (prev?.hasReview !== curr?.hasReview) {
          onUpdateChatInfo({
            hasReview: curr?.hasReview,
            menuStatus: setMenuStatus(curr)
          });
        }

        if (prev?.chatRoomStatus !== curr?.chatRoomStatus) {
          onUpdateChatInfo({
            chatRoomStatus: curr?.chatRoomStatus,
            menuStatus: setMenuStatus(curr)
          });
        }

        if (prev?.chatFiles?.length !== curr?.chatFiles?.length) {
          onUpdateChatInfo({
            chatFiles: curr?.chatFiles
          });
        }

        if (prev?.hasProgressingPayment !== curr?.hasProgressingPayment) {
          onUpdateChatInfo({
            hasProgressingPayment: curr?.hasProgressingPayment,
            menuStatus: setMenuStatus(curr)
          });
        }

        if (
          currPartnerLastSeenAt !== null &&
          prevPartnerLastSeenAt !== currPartnerLastSeenAt
        ) {
          onUpdateChatInfo({
            partnerLastSeenAt: new Date().toString()
          });
          onReadChatMessages();
        }

        return {
          ...prevResult,
          fetchQuotationChatRoom: {
            ...prevResult.fetchQuotationChatRoom,
            data: {
              ...prevResult.fetchQuotationChatRoom?.data,
              ...subscribeData.subscribeQuotationChatRoom?.data,
              partnerLastSeenAt: curr?.partnerLastSeenAt
                ? new Date().toString()
                : prev?.partnerLastSeenAt
            }
          }
        };
      }
    });

    //eslint-disable-next-line
  }, [chatRoomId]);

  const isLoading = !fetchData || loading;

  const isError =
    (!fetchData?.fetchQuotationChatRoom.data && !loading) ||
    (error ? true : false);

  useEffect(() => {
    if (!isLoading) {
      onUpdateChatInfo({
        ...fetchData?.fetchQuotationChatRoom.data,
        menuStatus: setMenuStatus({ ...fetchData?.fetchQuotationChatRoom.data })
      });
    }

    if (isLoading) {
      onInitChatInfo();
    }

    //eslint-disable-next-line
  }, [isLoading]);

  useEffect(() => {
    channelIo?.setAttribute('style', 'display: none;');

    return () => {
      channelIo?.setAttribute('style', 'display: block;');
    };
  }, [channelIo]);

  return { loading: isLoading, error: isError };
};

export default useSubscribeChatRoom;
